/**
 * ProseMirror schema for FOIA redaction editor
 * Defines custom redaction nodes and marks for interactive document editing
 */

import { Schema } from 'prosemirror-model';
import { nodes as basicNodes, marks as basicMarks } from 'prosemirror-schema-basic';
import { addListNodes } from 'prosemirror-schema-list';

// FOIA exemption categories with sub-exemptions
export const FOIA_EXEMPTIONS = [
  { 
    value: '(b)(1)', 
    label: 'National Security Information',
    description: 'Information properly classified for national defense or foreign policy',
    subExemptions: null 
  },
  { 
    value: '(b)(2)', 
    label: 'Internal Personnel Rules and Practices',
    description: 'Information related solely to internal personnel rules and practices',
    subExemptions: [
      { value: 'High 2', label: 'High 2 - Enforcement related' },
      { value: 'Low 2', label: 'Low 2 - Routine administrative' }
    ]
  },
  { 
    value: '(b)(3)', 
    label: 'Information Prohibited by Other Statutes',
    description: 'Information specifically exempted by another federal statute',
    subExemptions: null 
  },
  { 
    value: '(b)(4)', 
    label: 'Trade Secrets and Commercial Information',
    description: 'Trade secrets and privileged or confidential commercial/financial information',
    subExemptions: null 
  },
  { 
    value: '(b)(5)', 
    label: 'Privileged Communications',
    description: 'Inter-agency and intra-agency communications privileged in litigation',
    subExemptions: [
      { value: 'Attorney-Client', label: 'Attorney-Client Privilege' },
      { value: 'Work Product', label: 'Work Product Privilege' },
      { value: 'Deliberative Process', label: 'Deliberative Process Privilege' }
    ]
  },
  { 
    value: '(b)(6)', 
    label: 'Personal Privacy',
    description: 'Information that would constitute clearly unwarranted invasion of personal privacy',
    subExemptions: null 
  },
  { 
    value: '(b)(7)', 
    label: 'Law Enforcement Records',
    description: 'Records compiled for law enforcement purposes',
    subExemptions: [
      { value: '(A)', label: '(A) Interference with enforcement proceedings' },
      { value: '(B)', label: '(B) Deprive person of fair trial or impartial adjudication' },
      { value: '(C)', label: '(C) Unwarranted invasion of personal privacy' },
      { value: '(D)', label: '(D) Identity of confidential source' },
      { value: '(E)', label: '(E) Investigative techniques and procedures' },
      { value: '(F)', label: '(F) Endanger life or physical safety' }
    ]
  },
  { 
    value: '(b)(8)', 
    label: 'Financial Institution Supervision',
    description: 'Information related to examination of financial institutions',
    subExemptions: null 
  },
  { 
    value: '(b)(9)', 
    label: 'Geological Information on Wells',
    description: 'Geological and geophysical information concerning wells',
    subExemptions: null 
  }
];

// Custom redaction mark that wraps text
const redactionMark = {
  attrs: {
    id: { default: null },
    exemption: { default: '(b)(1)' },
    subExemption: { default: null },
    confidence: { default: 0 },
    status: { default: 'pending' }, // pending, approved, modified, rejected
    justification: { default: '' },
    userNotes: { default: '' },
    originalText: { default: '' },
    context: {
      default: {
        beforeText: '',
        afterText: ''
      }
    }
  },
  
  parseDOM: [{
    tag: 'span[data-redaction]',
    getAttrs(dom) {
      const data = JSON.parse(dom.getAttribute('data-redaction') || '{}');
      return {
        id: data.id || null,
        exemption: data.exemption || '(b)(1)',
        subExemption: data.subExemption || null,
        confidence: data.confidence || 0,
        status: data.status || 'pending',
        justification: data.justification || '',
        userNotes: data.userNotes || '',
        originalText: data.originalText || '',
        context: data.context || { beforeText: '', afterText: '' }
      };
    }
  }],
  
  toDOM(mark) {
    const { attrs } = mark;
    const statusClass = `redaction-${attrs.status}`;
    const exemptionClass = `exemption-${attrs.exemption.replace(/[()]/g, '').replace(/\s+/g, '-').toLowerCase()}`;
    
    return ['span', {
      class: `redaction ${statusClass} ${exemptionClass}`,
      'data-redaction': JSON.stringify(attrs),
      'data-exemption': attrs.exemption,
      'data-status': attrs.status,
      'data-confidence': attrs.confidence,
      title: `${attrs.exemption} - ${attrs.status} (${Math.round(attrs.confidence * 100)}%)`
    }];
  }
};

// Enhanced nodes with paragraph support for better document structure
let nodes;
try {
  nodes = addListNodes(basicNodes, 'paragraph block*', 'block');
} catch (e) {
  // Fallback to basic nodes if list nodes fail
  nodes = basicNodes;
}

// Enhanced marks with redaction support
const marks = {
  ...basicMarks,
  redaction: redactionMark
};

// Create the schema
export const redactionSchema = new Schema({ nodes, marks });

// Helper functions for working with redactions
export const RedactionUtils = {
  
  /**
   * Generate unique ID for redaction
   */
  generateId() {
    return `redaction-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Create a new redaction mark
   */
  createRedactionMark(attrs = {}) {
    return redactionSchema.marks.redaction.create({
      id: attrs.id || this.generateId(),
      exemption: attrs.exemption || '(b)(1)',
      subExemption: attrs.subExemption || null,
      confidence: attrs.confidence || 0,
      status: attrs.status || 'pending',
      justification: attrs.justification || '',
      userNotes: attrs.userNotes || '',
      originalText: attrs.originalText || '',
      context: attrs.context || { beforeText: '', afterText: '' }
    });
  },

  /**
   * Get exemption info by value
   */
  getExemptionInfo(exemptionValue) {
    return FOIA_EXEMPTIONS.find(ex => ex.value === exemptionValue);
  },

  /**
   * Get all redactions from a document
   */
  getRedactions(doc) {
    const redactions = [];
    
    doc.descendants((node, pos) => {
      if (node.marks) {
        node.marks.forEach(mark => {
          if (mark.type.name === 'redaction') {
            redactions.push({
              ...mark.attrs,
              startPos: pos,
              endPos: pos + node.nodeSize,
              text: node.textContent
            });
          }
        });
      }
    });
    
    return redactions.sort((a, b) => a.startPos - b.startPos);
  },

  /**
   * Update redaction attributes
   */
  updateRedaction(tr, redactionId, newAttrs) {
    const doc = tr.doc;
    
    doc.descendants((node, pos) => {
      if (node.marks) {
        node.marks.forEach((mark, markIndex) => {
          if (mark.type.name === 'redaction' && mark.attrs.id === redactionId) {
            const newMark = mark.type.create({
              ...mark.attrs,
              ...newAttrs
            });
            
            tr.removeMark(pos, pos + node.nodeSize, mark);
            tr.addMark(pos, pos + node.nodeSize, newMark);
          }
        });
      }
    });
    
    return tr;
  },

  /**
   * Remove redaction by ID
   */
  removeRedaction(tr, redactionId) {
    const doc = tr.doc;
    
    doc.descendants((node, pos) => {
      if (node.marks) {
        node.marks.forEach(mark => {
          if (mark.type.name === 'redaction' && mark.attrs.id === redactionId) {
            tr.removeMark(pos, pos + node.nodeSize, mark);
          }
        });
      }
    });
    
    return tr;
  },

  /**
   * Get context text around a position
   */
  getContext(doc, pos, sentencesBefore = 2, sentencesAfter = 2) {
    const text = doc.textContent;
    
    // Find sentence boundaries
    const sentences = text.split(/[.!?]+\s+/);
    let currentPos = 0;
    let sentenceIndex = 0;
    
    // Find which sentence contains our position
    for (let i = 0; i < sentences.length; i++) {
      const sentenceEnd = currentPos + sentences[i].length;
      if (pos >= currentPos && pos <= sentenceEnd) {
        sentenceIndex = i;
        break;
      }
      currentPos = sentenceEnd + 2; // Account for punctuation and space
    }
    
    // Get context sentences
    const startIndex = Math.max(0, sentenceIndex - sentencesBefore);
    const endIndex = Math.min(sentences.length - 1, sentenceIndex + sentencesAfter);
    
    const beforeText = sentences.slice(startIndex, sentenceIndex).join('. ');
    const afterText = sentences.slice(sentenceIndex + 1, endIndex + 1).join('. ');
    
    return { beforeText, afterText };
  }
};

export default redactionSchema;